/**
 * Conversation Streaming Facade (100% Shared)
 *
 * Wraps all streaming conversation-related state and actions.
 * This facade is completely platform-agnostic and can be used
 * by both web and plugin without any modifications.
 *
 * Responsibilities:
 * - Streaming conversation creation
 * - Message submission
 * - Conversation history loading
 * - Real-time message updates
 * - Stream cancellation
 *
 * Usage:
 * ```tsx
 * const streaming = useConversationStreamingFacade();
 * await streaming.createStreamingConversation({...});
 * ```
 *
 * @module useConversationStreamingFacade
 * @category Facades/Shared
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  submitUserMessage,
  addAIResponse,
  setConversationId,
  updateAIMessage,
  updateMessageFeedback,
  setLoading,
  addMessage,
  removeMessages,
  setMessages,
  selectConversationState,
  initializeConversation,
  setInitiatedFromUncover,
} from '../../../store/slices/conversationSlice';
import {
  selectCurrentProjectId,
  selectCurrentScopeId,
  selectCurrentDiscussionRoomId,
  getUserProfile,
  setCurrentScopeId,
} from '../../../store/slices/appSlice';
import { useAccessToken } from '../../useAccessToken';
import {
  createConversationStream,
  loadConversationHistory,
  conversationService,
  ensureProjectScope,
  createConversationShell,
} from '../../../api/services/conversation.service';
import { artifactService } from '../../../api/services/artifact.service';
import { deriveHasPrototypeFromFields } from '../../../utils/prototyperMessageUtils';
import type {
  ConversationHeaders,
  FirstChunkHeaders,
  ForcedArtifact,
  StreamingConversationParams,
  ConversationStepMode,
  Message,
  ConversationState,
  ConversationDetails,
  QuestionAnswerBody,
} from '../../../types';
import type { AgentStreamEvent, QuestionSetPayload } from '../../../types/sse-events';

export interface StreamProgress {
  phase?: string;
  nodeId?: string;
  toolName?: string;
}

/** Parse a streamed `format: "json"` artifact body (a JSON string) into an object. */
const parseJsonArtifactContent = (content: string): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(content);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

/**
 * One entry in the "thinking" feed shown above the assistant reply. Populated
 * from status / node_started / tool_call SSE events. UI renders them like the
 * expandable "Thinking…" panel in the Claude apps.
 *
 * `label` is the AI-supplied human-readable description (preferred). `id` is
 * the raw phase / node_id / tool_name — kept so the UI can fall back to a
 * humanised version if `label` isn't provided by the backend yet.
 */
export type ThoughtStep =
  | { kind: 'status'; id: string; label?: string; at: number }
  | { kind: 'node'; id: string; label?: string; at: number }
  | { kind: 'done'; id: string; label?: string; at: number }
  | { kind: 'tool'; id: string; label?: string; at: number };

export interface ClarificationPayload {
  question: string;
  options: string[];
  correlation_id: string;
}

export interface ConversationStreamingFacade {
  // ========== State ==========
  /**
   * Current conversation state from Redux
   */
  conversationState: ConversationState;

  /**
   * Whether the conversation has encountered an error
   */
  hasError: boolean;

  /**
   * Whether currently loading history
   */
  isLoadingHistory: boolean;

  /**
   * Whether currently streaming a response
   */
  isStreaming: boolean;

  /**
   * Live progress from status / node_started / tool_call SSE events
   */
  streamProgress: StreamProgress | null;

  /**
   * Ordered feed of "thinking" steps — one entry per status / node_started /
   * tool_call SSE event received during the current (or most recent) turn.
   * The ThinkingPanel component renders this above the assistant reply.
   * Cleared on each new streaming turn.
   */
  streamThoughts: ThoughtStep[];

  /**
   * Wall-clock ms elapsed from the first thinking step to when tokens started
   * (or the turn ended). Used to render the "Thought for Xs" summary chip
   * once the reasoning panel collapses. `null` until the panel has data.
   */
  thinkingDurationMs: number | null;

  /**
   * True once the assistant has started streaming visible tokens for this
   * turn. UI uses this to auto-collapse the thinking panel.
   */
  hasStartedResponse: boolean;

  /**
   * Latest clarification payload from SSE (question + MCQ options)
   */
  clarification: ClarificationPayload | null;

  // ========== Actions ==========
  /**
   * Create a streaming conversation
   */
  createStreamingConversation: (
    message: string,
    existingConversationId?: string,
    onComplete?: (newConversationId?: string, headers?: ConversationHeaders) => void,
    setIsStreaming?: (streaming: boolean) => void,
    attachmentIds?: string[],
    skipAnalysis?: boolean,
    isHiddenUserQuery?: boolean,
    actionType?: string,
    llmFamily?: string,
    modeOverride?: 'clarify' | 'prototyper' | 'strategy',
    autoPrompt?: boolean,
    answerBody?: QuestionAnswerBody,
    forcedArtifact?: ForcedArtifact
  ) => Promise<void>;

  /**
   * Submit a message to the conversation
   */
  submitMessage: (
    text: string,
    attachmentIds?: string[],
    element?: { label: string; screenshot?: string }
  ) => void;

  /**
   * The product type every prototyper turn in this conversation builds as, from the composer's
   * Generate Prototype menu. Sticky on purpose: the founder picks "Mobile App" once and each
   * turn after it stays mobile until they pick something else. `projects.projectType` lists the
   * types the startup offers and cannot answer "which one is this build".
   */
  productType: string | null;

  /** Set the product type for this conversation's prototyper turns. */
  setProductType: (productType: string | null) => void;

  /**
   * Latest interactive question set from SSE (the agent offering choices).
   */
  questionSet: QuestionSetPayload | null;

  /** Clear the interactive question set (after answering or dismissing). */
  clearQuestionSet: () => void;

  /**
   * Re-read the still-pending interactive question set from the conversation's
   * history and restore the MCQ card, WITHOUT touching messages. Used when
   * re-entering a conversation whose messages are already in Redux (so
   * `loadHistory` is skipped) but whose `questionSet` was lost with the
   * unmounted page — e.g. navigating to another route and back.
   */
  restorePendingQuestions: (conversationId: string, projectId?: string | null) => Promise<void>;

  /**
   * Why the last answer submit was rejected, when it was. The set stays open in
   * that case so the founder can correct it in place — a rejected submit is
   * rendered on the card rather than discarded.
   */
  questionAnswerError: string | null;

  /** Clear the rejected-submit message (on the next edit or submit). */
  clearQuestionAnswerError: () => void;

  /**
   * Answer an interactive question set — sends the selected option ids back on
   * the conversation that asked. `user_message` is sent empty.
   */
  submitAnswer: (
    answer: QuestionAnswerBody,
    conversationId?: string,
    onComplete?: (newConversationId?: string, headers?: ConversationHeaders) => void
  ) => Promise<void>;

  /**
   * Cancel ongoing streaming
   */
  cancelStreaming: () => void;

  /**
   * Load conversation history
   * @returns Promise with the loaded messages
   */
  loadHistory: (conversationId: string, projectId?: string | null) => Promise<Message[]>;

  /**
   * Fetch conversation details (title, project_id)
   */
  fetchConversationDetails: (conversationId: string) => Promise<ConversationDetails>;

  /**
   * Set error state
   */
  setHasError: (hasError: boolean) => void;

  /**
   * Clear clarification MCQ state (e.g. after user replies)
   */
  clearClarification: () => void;

  /**
   * Send feedback (like/dislike) for a message
   */
  sendFeedback: (params: {
    messageID: string;
    isLiked: boolean;
    projectId?: string;
  }) => Promise<void>;

  /**
   * Create a conversation shell for pending file uploads (scoped projects only).
   */
  ensureConversationShell: (
    modeOverride?: 'clarify' | 'prototyper' | 'strategy'
  ) => Promise<string | undefined>;

  /**
   * Register a listener for every SSE agent stream event (findings, etc.).
   * Pass null to clear.
   */
  setStreamEventListener: (listener: ((event: AgentStreamEvent) => void) | null) => void;
}

/**
 * Conversation Streaming Facade Hook
 *
 * Provides streaming conversation management capabilities.
 * 100% shared between web and plugin.
 */
export function useConversationStreamingFacade(): ConversationStreamingFacade {
  const dispatch = useAppDispatch();

  // State
  const conversationState = useAppSelector(selectConversationState);
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const currentScopeId = useAppSelector(selectCurrentScopeId);
  // Startup-level Discussion Room: when set, the turn targets the room — no
  // scope is attached/auto-created, and the conversation (Nest-created, already
  // carries discussionRoomId + strategy mode) drives Atlas routing on the AI.
  const currentDiscussionRoomId = useAppSelector(selectCurrentDiscussionRoomId);
  const accessToken = useAccessToken();
  const [hasError, setHasError] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isStreaming, setStreamingState] = useState(false);
  const [streamProgress, setStreamProgress] = useState<StreamProgress | null>(null);
  const [streamThoughts, setStreamThoughts] = useState<ThoughtStep[]>([]);
  const [thinkingDurationMs, setThinkingDurationMs] = useState<number | null>(null);
  const [hasStartedResponse, setHasStartedResponse] = useState<boolean>(false);
  const [clarification, setClarification] = useState<ClarificationPayload | null>(null);
  const [questionSet, setQuestionSet] = useState<QuestionSetPayload | null>(null);

  /**
   * The product type this conversation's prototyper turns build as (US 359).
   *
   * Kept per scope in localStorage so it survives a reload and a walk through the app: the
   * founder picks "Mobile App" once and everything after it is mobile until they change it.
   * A ref shadows the state because the request payload is assembled inside a callback that
   * closes over its own render's values, and a turn sent right after the pick would otherwise
   * carry the previous one.
   */
  const productTypeStorageKey = currentScopeId
    ? `ideoz_product_type_${currentScopeId}`
    : null;
  const [productType, setProductTypeState] = useState<string | null>(() => {
    if (typeof localStorage === 'undefined' || !productTypeStorageKey) return null;
    try {
      return localStorage.getItem(productTypeStorageKey);
    } catch {
      // Private window or blocked site data: the choice just does not persist.
      return null;
    }
  });
  const productTypeRef = useRef<string | null>(productType);

  const setProductType = useCallback(
    (next: string | null) => {
      productTypeRef.current = next;
      setProductTypeState(next);
      if (typeof localStorage === 'undefined' || !productTypeStorageKey) return;
      try {
        if (next) localStorage.setItem(productTypeStorageKey, next);
        else localStorage.removeItem(productTypeStorageKey);
      } catch {
        /* nothing to do: the choice holds for this session only */
      }
    },
    [productTypeStorageKey],
  );

  // Re-read when the founder moves to another scope, which has its own choice.
  useEffect(() => {
    if (typeof localStorage === 'undefined' || !productTypeStorageKey) return;
    try {
      const stored = localStorage.getItem(productTypeStorageKey);
      productTypeRef.current = stored;
      setProductTypeState(stored);
    } catch {
      /* leave whatever is in memory */
    }
  }, [productTypeStorageKey]);
  const [questionAnswerError, setQuestionAnswerError] = useState<string | null>(null);
  const thinkingStartRef = useRef<number | null>(null);

  // Refs
  const abortControllerRef = useRef<AbortController | null>(null);
  /**
   * Id of the most recent `loadHistory` request. If the user switches to another
   * conversation while a history load is in flight, this advances — and the
   * older, now-stale response is dropped instead of overwriting the newly
   * selected conversation's messages (the switch-while-pending race).
   */
  const latestHistoryConversationRef = useRef<string | null>(null);
  const streamEventListenerRef = useRef<((event: AgentStreamEvent) => void) | null>(null);
  /**
   * Id of the optimistic "here's what I picked" message for an answer in
   * flight, so it can be rolled back if the submit is rejected outright.
   */
  const answerMessageIdRef = useRef<string | null>(null);
  /** Prevents double turn-complete side effects */
  const turnFinalizedRef = useRef(false);

  const setStreamEventListener = useCallback(
    (listener: ((event: AgentStreamEvent) => void) | null) => {
      streamEventListenerRef.current = listener;
    },
    []
  );

  const clearClarification = useCallback(() => {
    setClarification(null);
  }, []);

  const clearQuestionSet = useCallback(() => {
    setQuestionSet(null);
    setQuestionAnswerError(null);
  }, []);

  const restorePendingQuestions = useCallback(
    async (conversationId: string, projectId?: string | null) => {
      try {
        const { pendingQuestions } = await loadConversationHistory(conversationId, projectId);
        // Only set when the backend still has a pending set. Never clear here: on
        // re-entry `questionSet` is already null (fresh mount), and we must not
        // clobber a set a live stream may have just delivered.
        if (pendingQuestions) setQuestionSet(pendingQuestions);
      } catch (err) {
        console.error('Failed to restore pending questions:', err);
      }
    },
    []
  );

  const clearQuestionAnswerError = useCallback(() => {
    setQuestionAnswerError(null);
  }, []);

  /**
   * Create a streaming conversation with real-time updates
   */
  const createStreamingConversation = useCallback(
    async (
      message: string,
      existingConversationId?: string,
      onCompleteCallback?: (newConversationId?: string, headers?: ConversationHeaders) => void,
      setIsStreaming?: (streaming: boolean) => void,
      attachmentIds?: string[],
      skipAnalysis?: boolean,
      isHiddenUserQuery?: boolean,
      actionType?: string,
      llmFamily?: string,
      modeOverride?: 'clarify' | 'prototyper' | 'strategy',
      autoPrompt?: boolean,
      answerBody?: QuestionAnswerBody,
      forcedArtifact?: ForcedArtifact
    ) => {
      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();
      turnFinalizedRef.current = false;

      dispatch(setLoading(true));
      setStreamingState(true);
      setIsStreaming?.(true);
      setStreamProgress(null);
      setStreamThoughts([]);
      setThinkingDurationMs(null);
      setHasStartedResponse(false);
      thinkingStartRef.current = null;
      // Per-turn "has any token arrived yet" flag. State + closure would
      // capture a stale value across the stream; a ref is authoritative
      // for the event handler while state still drives React renders.
      let hasStartedResponseThisTurn = false;
      // Local per-turn thoughts trail. Kept alongside the facade state so
      // we can also stamp it onto the message (message.thoughts) — that's
      // what makes per-turn history persist across subsequent turns.
      const thoughtsThisTurn: ThoughtStep[] = [];
      setClarification(null);
      // A regular turn invalidates any open set. An answer turn does not: the
      // card stays mounted until the backend has actually taken the answer
      // (first chunk / result), so a rejected submit has somewhere to render
      // instead of vanishing with the card.
      if (!answerBody) setQuestionSet(null);
      setHasError(false);

      const aiMessageId = (Date.now() + 1).toString();
      const initialAIMessage: Message = {
        id: aiMessageId,
        content: '',
        role: 'assistant',
      };
      dispatch(addMessage(initialAIMessage));

      let accumulatedContent = '';
      let firstChunkReceived = false;
      let detectedLanguage: 'rtl' | 'ltr' | undefined;
      let llmInteractionId: string | undefined;
      let storedConversationId: string | undefined;
      let storedHeaders: ConversationHeaders | undefined;
      let messageMode: 'clarify' | 'prototyper' | undefined;
      let sawResult = false;
      let sawError = false;
      let primaryArtifactName: string | undefined;
      // The streamed structured JSON, kept so the post-stream poll can preserve
      // it — the persisted row currently comes back with markdown only (no
      // `structured_content`), which would otherwise wipe the card.
      let primaryArtifactStructured: Record<string, unknown> | null = null;
      // File-backed metadata (PDF/DOCX/…) streamed on the artifact event, kept
      // so the post-stream hydration can fall back to it if the persisted row
      // hasn't surfaced these columns yet.
      let primaryArtifactFileFormat: string | null = null;
      let primaryArtifactFileName: string | null = null;
      let primaryArtifactMediaType: string | null = null;
      let sseClarification = false;

      // Discussion Room turns are not scoped: never auto-create/attach a scope.
      // The conversation (Nest-created) already carries discussionRoomId + the
      // strategy mode, so the AI resolves Atlas context from the conversation.
      let scopeId = currentDiscussionRoomId ? null : currentScopeId;
      if (!currentDiscussionRoomId && currentProjectId && !scopeId) {
        scopeId = await ensureProjectScope(currentProjectId);
        dispatch(setCurrentScopeId(scopeId));
      }

      // Mode is auto-selected by the AI on regular turns, so the client no longer
      // sends `mode`. Auto-prompt greetings still send the page mode so the first
      // scope conversation opens in clarify (Huem). Continuation auto-prompt
      // forces strategy (Atlas) on the backend when the scope already has history.
      const modeToSend: ConversationStepMode | undefined = autoPrompt
        ? (modeOverride ?? conversationState.mode ?? 'clarify')
        : undefined;

      const streamParams: StreamingConversationParams = {
        session_id: conversationState.session_id || undefined,
        ...(modeToSend ? { mode: modeToSend } : {}),
        // The backend owns the priming prompt for auto-prompt greetings, so we
        // omit user_message entirely in that case (empty string would be sent).
        // An interactive answer sends the ids with an empty user_message.
        ...(answerBody
          ? { user_message: '', answer: answerBody }
          : autoPrompt
            ? { auto_prompt: true }
            : { user_message: message }),
        // Spread after the message branch so it can never overwrite `user_message` or
        // `answer`. The backend only inlines these attachments when no prose and no
        // answer was sent; otherwise it already sees them via the conversation.
        ...(attachmentIds?.length ? { attachments_ids: attachmentIds } : {}),
        project_id: currentProjectId || undefined,
        scope_id: scopeId || undefined,
        ...(currentDiscussionRoomId
          ? { discussion_room_id: currentDiscussionRoomId }
          : {}),
        ...(existingConversationId && { conversation_id: existingConversationId }),
        ...(skipAnalysis && { skip_analysis: skipAnalysis }),
        ...(isHiddenUserQuery && { is_hidden_user_query: isHiddenUserQuery }),
        ...(actionType && { action_type: actionType }),
        platform_type: 'web',
        ...(llmFamily && { llm_family: llmFamily }),
        ...(forcedArtifact && { forced_artifact: forcedArtifact }),
        // Sent on every turn, not only the one that picked it, so an edit after "Mobile App"
        // stays mobile. The AI API prefers it over the project row when pinning the spec's mode.
        ...(productTypeRef.current ? { product_type: productTypeRef.current } : {}),
      };

      const mergeTurnHeaders = (base?: ConversationHeaders): ConversationHeaders => {
        const merged: ConversationHeaders = { ...(base || {}), ...(storedHeaders || {}) };
        if (sseClarification) {
          merged.clarification = true;
        }
        // `prototype_ready` SSE event is the new source of truth for the
        // prototyper flag — force it on when the stream said so, so the
        // downstream headers facade opens the prototyper panel regardless
        // of whether the legacy `X-Prototyper` header was sent.
        if (messageMode === 'prototyper') {
          merged.prototyper = true;
        }
        return merged;
      };

      const finalizeTurnSuccess = (conversationId?: string, headers?: ConversationHeaders) => {
        if (turnFinalizedRef.current) return;
        turnFinalizedRef.current = true;

        // Belt-and-braces for the answer turn: if the first-chunk meta never
        // arrived, the result event is still proof the answer was accepted.
        // A `question` event this turn (a follow-up set) has already replaced
        // `questionSet`, so only clear the one we submitted against.
        if (answerBody) {
          setQuestionSet((prev) =>
            prev && prev.question_set_id === answerBody.question_set_id ? null : prev
          );
          setQuestionAnswerError(null);
          answerMessageIdRef.current = null;
        }

        const finalHeaders = mergeTurnHeaders(headers);
        storedHeaders = finalHeaders;
        setStreamProgress(null);
        // If we never emitted a token this turn (e.g. tool-only result),
        // freeze the thinking duration here so the panel can show a
        // "Thought for Xs" summary.
        if (thinkingStartRef.current != null && !hasStartedResponseThisTurn) {
          const frozen = Date.now() - thinkingStartRef.current;
          setThinkingDurationMs(frozen);
          dispatch(
            updateAIMessage({
              messageId: aiMessageId,
              content: accumulatedContent,
              thinkingDurationMs: frozen,
            })
          );
        }

        dispatch(setLoading(false));
        setStreamingState(false);
        setIsStreaming?.(false);

        onCompleteCallback?.(conversationId || storedConversationId, finalHeaders);

        // Hydrate the just-streamed artifact with its persisted id + demo_url.
        // The AI streams the artifact body in real time, but the DB row is
        // written asynchronously by a Celery task. Poll a few times so the
        // side panel's Edit / Share / Save buttons light up on this turn
        // (they gate on `artifact.id`) instead of only after a page refresh.
        if (llmInteractionId && primaryArtifactName) {
          const interactionId = llmInteractionId;
          const messageId = aiMessageId;
          void (async () => {
            for (let attempt = 0; attempt < 5; attempt += 1) {
              await new Promise((r) => setTimeout(r, 800));
              try {
                const rows = await artifactService.listArtifactsByLlmInteraction(interactionId);
                const row = rows[0];
                if (!row) continue;
                // Prefer the row's structured JSON; fall back to the JSON we
                // streamed this turn (the row may only carry markdown until the
                // backend returns `structured_content`), so the card survives.
                const structured = row.structured_content ?? primaryArtifactStructured;
                dispatch(
                  updateAIMessage({
                    messageId,
                    content: accumulatedContent,
                    artifact: {
                      id: row.id,
                      name: row.artifact_type || primaryArtifactName || 'artifact',
                      content: row.content ?? '',
                      format: structured ? 'json' : 'markdown',
                      structuredContent: structured,
                      demoUrl: row.demo_url ?? null,
                      // Preserve the file-backed metadata (PDF/DOCX/…) so the
                      // side panel keeps downloading the real binary instead of
                      // falling back to the markdown `content`.
                      fileFormat: row.file_format ?? primaryArtifactFileFormat ?? null,
                      fileName: row.file_name ?? primaryArtifactFileName ?? null,
                      mediaType: row.media_type ?? primaryArtifactMediaType ?? null,
                      fileStatus: row.file_status ?? null,
                    },
                  })
                );
                return;
              } catch {
                // ignore; retry
              }
            }
          })();
        }

        if (accessToken) {
          dispatch(getUserProfile(accessToken)).catch(() => {
            // Silent failure - don't interrupt user flow
          });
        }
      };

      await createConversationStream(
        streamParams,
        // onEvent
        (
          event: AgentStreamEvent,
          meta?: { firstChunkHeaders?: FirstChunkHeaders; allHeaders?: ConversationHeaders }
        ) => {
          if (meta?.firstChunkHeaders) {
            if (meta.firstChunkHeaders.headers?.detectedLanguage) {
              detectedLanguage = meta.firstChunkHeaders.headers.detectedLanguage;
            }
            if (meta.firstChunkHeaders.llmInteractionId) {
              llmInteractionId = meta.firstChunkHeaders.llmInteractionId;
            }
            // Deliberately NOT reading `meta.allHeaders?.prototyper` (the legacy
            // `X-Prototyper` header) here. That header flips true as soon as Buildo
            // is the *dispatched* super-agent for the turn — before Buildo has
            // decided whether this turn actually produces a build (it may still ask
            // a clarifying question instead). Only the `prototype_ready` SSE event
            // below confirms a real TaskFlowSpec was produced, so it's the only
            // thing allowed to set `messageMode = 'prototyper'`.
          }

          switch (event.type) {
            case 'token': {
              // First visible token: mark the thinking panel as ready to
              // collapse and freeze the elapsed thinking time.
              let frozenDuration: number | undefined;
              if (!hasStartedResponseThisTurn) {
                hasStartedResponseThisTurn = true;
                setHasStartedResponse(true);
                if (thinkingStartRef.current != null) {
                  frozenDuration = Date.now() - thinkingStartRef.current;
                  setThinkingDurationMs(frozenDuration);
                }
              }
              accumulatedContent += event.payload.text;
              dispatch(
                updateAIMessage({
                  messageId: aiMessageId,
                  content: accumulatedContent.replace(/^[,\s\n\r]+/, ''),
                  ...(detectedLanguage && { detectedLanguage }),
                  ...(llmInteractionId && { llmInteractionId }),
                  ...(messageMode && { mode: messageMode }),
                  ...(frozenDuration != null && { thinkingDurationMs: frozenDuration }),
                })
              );
              dispatch(setLoading(false));
              break;
            }

            case 'result': {
              sawResult = true;
              accumulatedContent = event.payload.content;
              dispatch(
                updateAIMessage({
                  messageId: aiMessageId,
                  content: accumulatedContent.trim(),
                  ...(detectedLanguage && { detectedLanguage }),
                  ...(llmInteractionId && { llmInteractionId }),
                  ...(messageMode && { mode: messageMode }),
                })
              );
              finalizeTurnSuccess(storedConversationId, storedHeaders);
              break;
            }

            case 'error':
              sawError = true;
              setStreamProgress(null);
              if (!accumulatedContent.trim()) {
                dispatch(
                  updateAIMessage({
                    messageId: aiMessageId,
                    content: event.payload.message,
                    hasBanner: true,
                    bannerType: 'error',
                    bannerPosition: 'bottom',
                    bannerText: event.payload.message,
                  })
                );
                setHasError(true);
              }
              dispatch(setLoading(false));
              setStreamingState(false);
              setIsStreaming?.(false);
              break;

            case 'status': {
              setStreamProgress((prev) => ({
                ...prev,
                phase: event.payload.phase,
              }));
              if (thinkingStartRef.current == null) thinkingStartRef.current = Date.now();
              // Only surface status events that carry a human label — the
              // technical phases (`loading_conversation`, `conversation_loaded`,
              // `starting`) would otherwise litter the trail.
              const statusLabel = event.payload.label?.trim();
              if (!statusLabel) break;
              const step: ThoughtStep = {
                kind: 'status',
                id: event.payload.phase,
                label: statusLabel,
                at: Date.now(),
              };
              thoughtsThisTurn.push(step);
              setStreamThoughts([...thoughtsThisTurn]);
              dispatch(
                updateAIMessage({
                  messageId: aiMessageId,
                  content: accumulatedContent,
                  thoughts: [...thoughtsThisTurn],
                })
              );
              break;
            }

            case 'node_started': {
              setStreamProgress((prev) => ({
                ...prev,
                nodeId: event.payload.node_id,
              }));
              if (thinkingStartRef.current == null) thinkingStartRef.current = Date.now();
              const step: ThoughtStep = {
                kind: 'node',
                id: event.payload.node_id,
                label: event.payload.label,
                at: Date.now(),
              };
              thoughtsThisTurn.push(step);
              setStreamThoughts([...thoughtsThisTurn]);
              dispatch(
                updateAIMessage({
                  messageId: aiMessageId,
                  content: accumulatedContent,
                  thoughts: [...thoughtsThisTurn],
                })
              );
              break;
            }

            case 'node_completed': {
              // Only render completions that carry a *meaningful* summary
              // (e.g. "Dispatched 1 super agent"). Empty/null labels are the
              // common case and duplicate the started event, so drop them.
              const doneLabel = event.payload.label?.trim();
              if (!doneLabel) break;
              const step: ThoughtStep = {
                kind: 'done',
                id: event.payload.node_id,
                label: doneLabel,
                at: Date.now(),
              };
              thoughtsThisTurn.push(step);
              setStreamThoughts([...thoughtsThisTurn]);
              dispatch(
                updateAIMessage({
                  messageId: aiMessageId,
                  content: accumulatedContent,
                  thoughts: [...thoughtsThisTurn],
                })
              );
              break;
            }

            case 'tool_call': {
              setStreamProgress((prev) => ({
                ...prev,
                toolName: event.payload.tool_name,
              }));
              if (thinkingStartRef.current == null) thinkingStartRef.current = Date.now();
              const step: ThoughtStep = {
                kind: 'tool',
                id: event.payload.tool_name,
                label: event.payload.label,
                at: Date.now(),
              };
              thoughtsThisTurn.push(step);
              setStreamThoughts([...thoughtsThisTurn]);
              dispatch(
                updateAIMessage({
                  messageId: aiMessageId,
                  content: accumulatedContent,
                  thoughts: [...thoughtsThisTurn],
                })
              );
              break;
            }

            case 'clarification':
              sseClarification = true;
              setClarification({
                question: event.payload.question,
                // Choices moved to the `question` event — never rendered here.
                options: event.payload.options ?? [],
                correlation_id: event.payload.correlation_id,
              });
              break;

            case 'question':
              // Interactive question set — the agent offering choices with ids.
              // Rendered as a form; answered via `submitAnswer`.
              setQuestionSet(event.payload);
              break;

            case 'artifact': {
              if (!primaryArtifactName && event.payload.artifact_name) {
                primaryArtifactName = event.payload.artifact_name;
              }
              // Store the full artifact body on the current assistant
              // message so ArtifactPill (inline) and the artifact side
              // panel can render it — the header-driven flow used to
              // fetch this via `artifact_demo_link_id`, but the AI now
              // streams the content directly in the SSE event.
              const {
                artifact_name,
                content,
                format,
                super_agent_id,
                source_tool_id,
                id: artifactId,
                file_format,
                file_name,
                media_type,
                file_status,
              } = event.payload;
              if (content) {
                // Structured artifacts arrive as a JSON string in `content` with
                // `format: "json"` — parse it once so the card renders from the
                // typed payload. Non-JSON (legacy markdown) passes through.
                const structuredContent =
                  format === 'json' ? parseJsonArtifactContent(content) : null;
                if (structuredContent) primaryArtifactStructured = structuredContent;
                if (file_format) primaryArtifactFileFormat = file_format;
                if (file_name) primaryArtifactFileName = file_name;
                if (media_type) primaryArtifactMediaType = media_type;
                dispatch(
                  updateAIMessage({
                    messageId: aiMessageId,
                    content: accumulatedContent,
                    artifact: {
                      id: artifactId ?? undefined,
                      name: artifact_name,
                      content,
                      format: format ?? null,
                      structuredContent,
                      superAgentId: super_agent_id ?? null,
                      sourceToolId: source_tool_id ?? null,
                      fileFormat: file_format ?? null,
                      fileName: file_name ?? null,
                      mediaType: media_type ?? null,
                      fileStatus: file_status ?? null,
                    },
                  })
                );
              }
              break;
            }

            case 'prototype_ready': {
              // The AI now signals a prototyper turn via the SSE stream
              // (was: `X-Prototyper: true` response header). Flip the
              // message mode + stamp the mode on the current AI message so
              // downstream headers/pill logic treats this as a prototype.
              // Also mark `hasPrototype` so a "View prototype" pill renders
              // under this message even after a refresh, without waiting
              // for `prototyper_demo_link_id` (which only lands after the
              // client-side build finishes).
              messageMode = 'prototyper';
              dispatch(
                updateAIMessage({
                  messageId: aiMessageId,
                  content: accumulatedContent,
                  mode: 'prototyper',
                  hasPrototype: true,
                  prototypeReady: true,
                  prototypeSuperAgentId: event.payload.super_agent_id,
                  // Buildo's product name, so the pill reads correctly from the first
                  // moment rather than after the next history load.
                  prototypeTitle: event.payload.prototype_title ?? undefined,
                })
              );
              break;
            }

            case 'contradiction':
            case 'assumption':
            case 'key_insight':
            case 'finding_status_changed':
            case 'verdict_recomputed':
              // Handled via streamEventListener (findings facade)
              break;
          }

          streamEventListenerRef.current?.(event);

          // First-chunk meta: store IDs only — do NOT treat as turn complete
          if (meta?.firstChunkHeaders && !firstChunkReceived) {
            setHasError(false);
            firstChunkReceived = true;
            // The answer was taken (we're streaming the reply to it) — the card
            // has done its job and the optimistic message stands. Only the set
            // we answered is cleared; a follow-up set from this same turn stays.
            if (answerBody) {
              setQuestionSet((prev) =>
                prev && prev.question_set_id === answerBody.question_set_id ? null : prev
              );
              setQuestionAnswerError(null);
              answerMessageIdRef.current = null;
            }
            storedConversationId = meta.firstChunkHeaders.conversationId;
            // Same reasoning as above: don't let the raw legacy `X-Prototyper`
            // header seed `storedHeaders.prototyper` — `mergeTurnHeaders` only ever
            // forces it *true* (from a confirmed `prototype_ready` event via
            // `messageMode`), so a `true` written here from the header would leak
            // straight through unfiltered for a turn that never actually built.
            storedHeaders = meta.allHeaders
              ? { ...meta.allHeaders, prototyper: false }
              : meta.allHeaders;

            if (meta.firstChunkHeaders.conversationId) {
              dispatch(setConversationId(meta.firstChunkHeaders.conversationId));
            }
          }
        },
        // onError
        (error: Error) => {
          console.error('Streaming conversation error:', error);
          setStreamProgress(null);

          if (error.name === 'AbortError') {
            // User cancel — do not finalize turn side effects
          } else if (answerBody && !accumulatedContent.trim()) {
            // The answer never reached the agent (a validation 400, typically).
            // Nothing was persisted and the questions are still pending, so roll
            // back the optimistic pair and put the rejection on the card — which
            // is still mounted — for the founder to correct in place.
            const stale = [aiMessageId, answerMessageIdRef.current].filter(
              (id): id is string => !!id
            );
            dispatch(removeMessages(stale));
            answerMessageIdRef.current = null;
            setQuestionAnswerError(
              error.message || 'That answer could not be sent. Please try again.'
            );
          } else {
            if (!accumulatedContent.trim()) {
              const errorStatus = (error as Error & { status?: number }).status;
              const errorMessage =
                error.message || 'We are facing some trouble, please try again later';
              const shouldShowErrorBanner = errorStatus === 403 && !!errorMessage;

              dispatch(
                updateAIMessage({
                  messageId: aiMessageId,
                  content: shouldShowErrorBanner
                    ? errorMessage
                    : 'We are facing some trouble, please try again later',
                  ...(shouldShowErrorBanner && {
                    hasBanner: true,
                    bannerType: 'error',
                    bannerPosition: 'bottom',
                    bannerText: errorMessage,
                  }),
                })
              );
              setHasError(true);
            }
            // Do not call onComplete on transport error if we never got result
          }
          dispatch(setLoading(false));
          setStreamingState(false);
          setIsStreaming?.(false);
        },
        // onComplete — stream reader finished; only cleanup if not already finalized via result
        (newConversationId?: string, headers?: ConversationHeaders) => {
          storedConversationId = newConversationId || storedConversationId;
          storedHeaders = headers || storedHeaders;

          if (newConversationId && !firstChunkReceived) {
            dispatch(setConversationId(newConversationId));
          }

          // Already finalized on result — just ensure loading flags are clear
          if (sawResult || turnFinalizedRef.current) {
            // `prototype_ready` can arrive after `result` (it's a mid-stream SSE event now,
            // not a response header available before the stream starts) — when that happens,
            // `finalizeTurnSuccess` already ran and reported `headers.prototyper: false` to the
            // caller before this turn's true prototyper status was known. Correct that once,
            // here, now that every event (including a late `prototype_ready`) has been
            // processed — otherwise the message renders its "View prototype" pill (set
            // directly by the `prototype_ready` case, independent of finalization) while the
            // facade's `isPrototyper` flag stays false, so the panel never auto-opens and the
            // build-stream never starts, leaving the pill pointing at a snapshot that was
            // never built.
            if (messageMode === 'prototyper' && !storedHeaders?.prototyper) {
              const correctedHeaders: ConversationHeaders = {
                ...(storedHeaders ?? {}),
                prototyper: true,
              };
              storedHeaders = correctedHeaders;
              onCompleteCallback?.(storedConversationId, correctedHeaders);
            }
            dispatch(setLoading(false));
            setStreamingState(false);
            setIsStreaming?.(false);
            setStreamProgress(null);
            return;
          }

          // Stream ended without result/error — leave tokens as incomplete; no side effects,
          // except for the same prototyper-headers correction applied above: `prototype_ready`
          // may have already dispatched the "View prototype" pill directly, independent of
          // finalization, so without this the facade's `isPrototyper` flag never flips true
          // and the build-stream auto-start stays permanently blocked for this turn.
          if (!sawError) {
            console.warn('Stream ended without result event — not finalizing turn side effects');
            if (messageMode === 'prototyper' && !storedHeaders?.prototyper) {
              const correctedHeaders: ConversationHeaders = {
                ...(storedHeaders ?? {}),
                prototyper: true,
              };
              storedHeaders = correctedHeaders;
              onCompleteCallback?.(storedConversationId, correctedHeaders);
            }
          }
          dispatch(setLoading(false));
          setStreamingState(false);
          setIsStreaming?.(false);
          setStreamProgress(null);
        },
        abortControllerRef.current?.signal
      );
    },
    [
      dispatch,
      conversationState.session_id,
      conversationState.mode,
      currentProjectId,
      currentScopeId,
      accessToken,
    ]
  );

  /**
   * Submit a message to the conversation
   */
  const submitMessage = useCallback(
    (text: string, attachmentIds?: string[], element?: { label: string; screenshot?: string }) => {
      if (!text.trim() && (!attachmentIds || attachmentIds.length === 0)) return;
      dispatch(submitUserMessage({ text, attachmentIds, element }));
    },
    [dispatch]
  );

  /**
   * Answer an interactive question set. Streams the answer on the conversation
   * that asked (empty `user_message` + the `answer` ids). The set is cleared
   * only once the backend has taken the answer — a rejected submit keeps the
   * card mounted, with `questionAnswerError` set, so the founder can fix it.
   */
  const submitAnswer = useCallback(
    async (
      answer: QuestionAnswerBody,
      conversationId?: string,
      onComplete?: (newConversationId?: string, headers?: ConversationHeaders) => void
    ) => {
      setQuestionAnswerError(null);
      answerMessageIdRef.current = null;
      // Show the founder's selection as a user message (display-only — the real
      // answer goes to the backend via the `answer` ids, never as prose). A
      // declined question contributes no text and is omitted here; the backend
      // renders it to the planner as "— (declined)".
      const set = questionSet;
      if (set) {
        const questionAnswers = set.questions
          .map((q) => {
            const a = answer.answers.find((x) => x.question_id === q.question_id);
            if (!a) return null;
            const labels = a.selected_option_ids
              .map((id) => q.options.find((o) => o.id === id)?.label ?? id)
              .filter(Boolean);
            const parts = [...labels];
            if (a.custom_text) parts.push(a.custom_text);
            const answerText = parts.join(', ');
            if (!answerText) return null; // skipped question — omit
            return { question: q.question, answer: answerText };
          })
          .filter((x): x is { question: string; answer: string } => !!x);
        if (questionAnswers.length > 0) {
          const answerMessageId = `qa-${Date.now()}`;
          answerMessageIdRef.current = answerMessageId;
          dispatch(
            addMessage({
              id: answerMessageId,
              content: '',
              role: 'user',
              questionAnswers,
            })
          );
        }
      }
      // The set is deliberately left mounted here — it is cleared once the
      // backend accepts the answer (first chunk / result), and kept with an
      // error when it doesn't.
      await createStreamingConversation(
        '', // user_message empty — the answer carries the selection
        conversationId || conversationState.conversationId || undefined,
        onComplete,
        undefined, // setIsStreaming (handled internally)
        undefined, // attachmentIds
        false, // skipAnalysis
        undefined, // isHiddenUserQuery
        undefined, // actionType
        conversationState.llmFamily,
        undefined, // modeOverride
        false, // autoPrompt
        answer
      );
    },
    [
      createStreamingConversation,
      conversationState.conversationId,
      conversationState.llmFamily,
      questionSet,
      dispatch,
    ]
  );

  /**
   * Cancel ongoing streaming
   */
  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    turnFinalizedRef.current = true; // prevent late finalize
    setStreamProgress(null);
    dispatch(setLoading(false));
    setStreamingState(false);
  }, [dispatch]);

  /**
   * Load conversation history
   */
  const loadHistory = useCallback(
    async (conversationId: string, projectId?: string | null) => {
      // Mark this as the latest history request; a later switch advances it and
      // invalidates this one (see the guard before dispatching below).
      latestHistoryConversationRef.current = conversationId;
      setIsLoadingHistory(true);
      // Clear any MCQ panel from the previous conversation immediately — it must
      // vanish with the outgoing messages, not linger through the skeleton. The
      // incoming chat's own pending set (if any) is restored below after load.
      setQuestionSet(null);
      try {
        const { messages, pendingQuestions } = await loadConversationHistory(
          conversationId,
          projectId
        );
        // Convert API messages to Message format and add IDs
        const formattedMessages: Message[] = messages.map((msg, index) => ({
          id: msg.id,
          content: msg.content,
          role: msg.role,
          attachments_ids: msg.attachments_ids,
          detectedLanguage:
            msg.detectedLanguage === 'ar'
              ? 'rtl'
              : msg.detectedLanguage === 'en'
                ? 'ltr'
                : undefined,
          // Preserve header-related fields from API
          actionType: msg.actionType,
          nodeType: msg.nodeType,
          mode: msg.mode,
          llmInteractionId: msg.llmInteractionId, // Preserve real backend UUID
          isLiked: msg.isLiked, // Preserve feedback state
          prototyper_demo_link_id: msg.prototyper_demo_link_id, // Preserve prototyper ID
          artifact_demo_link_id: (msg as any).artifact_demo_link_id,
          buildTimeline: msg.buildTimeline,
          buildStatus: msg.buildStatus,
          prototypeDisplayName: msg.prototypeDisplayName,
          prototypeTitle: msg.prototypeTitle,
          prototypeKind: msg.prototypeKind,
          prototypeVersionIndex: msg.prototypeVersionIndex,
          prototypeSnapshotId: msg.prototypeSnapshotId,
          prototypeReady: msg.prototypeReady,
          hasPrototype:
            msg.hasPrototype ??
            deriveHasPrototypeFromFields({
              prototypeReady: msg.prototypeReady,
              prototyper_demo_link_id: msg.prototyper_demo_link_id,
              buildTimeline: msg.buildTimeline,
              buildStatus: msg.buildStatus,
              prototypeSnapshotId: msg.prototypeSnapshotId,
            }),
          // Artifact (pill + card) now arrives inline on each interaction in the
          // llm-interactions history response — see `loadConversationHistory`.
          // No separate by-scope fetch needed.
          artifact: (msg as any).artifact,
          // Store additional metadata in message if needed
          ...(msg.nodeType && { drawType: msg.nodeType }),
        }));

        // A newer conversation switch superseded this request while it was in
        // flight — drop the stale response so it can't clobber the conversation
        // the user is now viewing.
        if (latestHistoryConversationRef.current !== conversationId) {
          return formattedMessages;
        }

        dispatch(setMessages(formattedMessages));

        // Restore the interactive MCQ card after a reload / navigation: the
        // history response carries the still-pending question set on its last
        // interaction (see `loadConversationHistory`). It streams live on the SSE
        // `question` event during a turn, but that in-memory set is lost on
        // refresh — this redraws it. `pendingQuestions` is null when nothing is
        // pending, which also clears any stale set from the previous chat.
        setQuestionSet(pendingQuestions);

        // Set initiatedFromUncover flag based on first assistant message's mode
        const firstAssistantMessage = formattedMessages.find((msg) => msg.role === 'assistant');
        if (firstAssistantMessage && firstAssistantMessage.mode === 'clarify') {
          dispatch(setInitiatedFromUncover(false));
        } else {
          dispatch(setInitiatedFromUncover(false));
        }

        return formattedMessages;
      } catch (error) {
        console.error('Failed to load conversation history:', error);
        throw error;
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [dispatch, currentScopeId]
  );

  /**
   * Fetch conversation details (title, project_id)
   */
  const fetchConversationDetails = useCallback(
    async (conversationId: string): Promise<ConversationDetails> => {
      try {
        const details = await conversationService.getConversationById(conversationId);
        return details;
      } catch (error) {
        console.error('Failed to fetch conversation details:', error);
        throw error;
      }
    },
    []
  );

  const sendFeedback = useCallback(
    async (params: { messageID: string; isLiked: boolean; projectId?: string }): Promise<void> => {
      // Find message by llmInteractionId to get the actual message ID
      const message = conversationState.messages.find(
        (msg) => (msg as any).llmInteractionId === params.messageID || msg.id === params.messageID
      );

      // Store original state for potential rollback
      const originalIsLiked = message ? (message as any).isLiked : undefined;

      try {
        // Optimistically update UI
        if (message) {
          dispatch(
            updateMessageFeedback({
              messageId: message.id,
              isLiked: params.isLiked,
            })
          );
        }

        // Send to backend
        await conversationService.sendLlmInteractionFeedback(params);
      } catch (error) {
        console.error('Failed to send feedback:', error);

        // Revert optimistic update on error
        if (message) {
          dispatch(
            updateMessageFeedback({
              messageId: message.id,
              isLiked: originalIsLiked,
            })
          );
        }

        throw error;
      }
    },
    [conversationState.messages, dispatch]
  );

  const ensureConversationShell = useCallback(
    async (modeOverride?: 'clarify' | 'prototyper' | 'strategy'): Promise<string | undefined> => {
      if (conversationState.conversationId) {
        return conversationState.conversationId;
      }
      if (!currentProjectId) {
        return undefined;
      }

      let scopeId = currentScopeId;
      if (!scopeId) {
        scopeId = await ensureProjectScope(currentProjectId);
        dispatch(setCurrentScopeId(scopeId));
      }

      const shellMode = modeOverride ?? conversationState.mode;
      const shell = await createConversationShell(currentProjectId, scopeId, shellMode);
      dispatch(setConversationId(shell.id));
      return shell.id;
    },
    [
      conversationState.conversationId,
      conversationState.mode,
      currentProjectId,
      currentScopeId,
      dispatch,
    ]
  );

  return {
    // State
    conversationState,
    hasError,
    isLoadingHistory,
    isStreaming,
    streamProgress,
    streamThoughts,
    thinkingDurationMs,
    hasStartedResponse,
    clarification,
    questionSet,
    questionAnswerError,

    // Actions
    createStreamingConversation,
    productType,
    setProductType,
    submitMessage,
    submitAnswer,
    cancelStreaming,
    loadHistory,
    fetchConversationDetails,
    setHasError,
    clearClarification,
    clearQuestionSet,
    restorePendingQuestions,
    clearQuestionAnswerError,
    sendFeedback,
    ensureConversationShell,
    setStreamEventListener,
  };
}
